import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { format } from 'date-fns';

import { AgaveDataGrid } from '@/components/agave-data-grid';
import Breadcrumb from '@/components/breadcrumb';
import type { SerialValidationRecord, SerialValidationStatus } from '@/domain/izzi';

import StatusChip from '../status-chip';
import { useSeriesSection } from './use-series-section';

const fmtDate = (value: unknown) =>
  value ? format(new Date(value as string), 'dd/MM/yyyy HH:mm') : '—';

const GRID_SX = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    borderBottom: '1px solid #f3f4f6',
    color: '#4b5563',
    fontSize: '0.875rem',
  },
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
  '& .MuiDataGrid-row': {
    borderBottom: '1px solid #f3f4f6',
    '&:hover': { backgroundColor: '#f9fafb' },
  },
  '& .MuiDataGrid-cell': { borderBottom: 'none', display: 'flex', alignItems: 'center' },
  '& .MuiDataGrid-footerContainer': { borderTop: 'none' },
};

export default function SeriesSection() {
  const hooks = useSeriesSection();

  // ── Definición de columnas por tab ─────────────────────────────────────

  const poColumns: GridColDef<SerialValidationRecord>[] = [
    { field: 'iccid', headerName: 'ICCID / Núm. Serie', flex: 1, minWidth: 200 },
    { field: 'sku', headerName: 'SKU', width: 130 },
    {
      field: 'status',
      headerName: 'Estado',
      width: 130,
      renderCell: ({ value }) => <StatusChip status={value as SerialValidationStatus} />,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de escaneo',
      width: 160,
      renderCell: ({ value }) => fmtDate(value),
    },
    { field: 'izziMessage', headerName: 'Mensaje Izzi', flex: 2, minWidth: 200 },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Desvincular serie">
          <span>
            <IconButton
              color="error"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                hooks.onDelete(params.row.id);
              }}
              disabled={hooks.isLoading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  const quarantinedColumns: GridColDef<SerialValidationRecord>[] = [
    { field: 'iccid', headerName: 'ICCID / Núm. Serie', flex: 1, minWidth: 200 },
    { field: 'purchaseOrder', headerName: 'Orden de compra', width: 160 },
    { field: 'sku', headerName: 'SKU', width: 110 },
    { field: 'corId', headerName: 'CorID', width: 120 },
    { field: 'customerSO', headerName: 'Customer SO', width: 130 },
    { field: 'izziMessage', headerName: 'Mensaje Izzi', flex: 2, minWidth: 200 },
    {
      field: 'quarantinedAt',
      headerName: 'En cuarentena desde',
      width: 180,
      renderCell: ({ value }) => fmtDate(value),
    },
  ];

  const releasedColumns: GridColDef<SerialValidationRecord>[] = [
    { field: 'iccid', headerName: 'ICCID / Núm. Serie', flex: 1, minWidth: 200 },
    { field: 'purchaseOrder', headerName: 'Orden de compra', width: 160 },
    { field: 'sku', headerName: 'SKU', width: 110 },
    {
      field: 'status',
      headerName: 'Estado final',
      width: 130,
      renderCell: ({ value }) => <StatusChip status={value as SerialValidationStatus} />,
    },
    { field: 'izziMessage', headerName: 'Mensaje Izzi', flex: 2, minWidth: 200 },
    {
      field: 'releasedAt',
      headerName: 'Liberada el',
      width: 170,
      renderCell: ({ value }) => fmtDate(value),
    },
    {
      field: 'revalidatedAt',
      headerName: 'Revalidada el',
      width: 170,
      renderCell: ({ value }) => fmtDate(value),
    },
  ];

  const errorColumns: GridColDef<SerialValidationRecord>[] = [
    { field: 'purchaseOrder', headerName: 'PO', flex: 1, minWidth: 120 },
    { field: 'sku', headerName: 'SKU', width: 130 },
    { field: 'iccid', headerName: 'ICCID / Núm. Serie', flex: 1, minWidth: 180 },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 160,
      renderCell: ({ value }) => fmtDate(value),
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: () => (
        <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
          Error
        </span>
      ),
    },
    {
      field: 'izziMessage',
      headerName: 'Descripción del error',
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="error" className="truncate">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Revalidar serie">
          <span>
            <IconButton
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                hooks.onRevalidate(params.row.id);
              }}
              disabled={hooks.isLoading}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  // ── Helpers de render ──────────────────────────────────────────────────

  const showFilter = hooks.activeTab !== 3;
  const filterLabel =
    hooks.activeTab === 0 ? 'Orden de compra (requerida)' : 'Orden de compra (opcional)';

  const currentRows = () => {
    if (hooks.activeTab === 0) return hooks.poData.rows;
    if (hooks.activeTab === 1) return hooks.quarData.rows;
    if (hooks.activeTab === 2) return hooks.relData.rows;
    return hooks.errData.rows;
  };

  const currentTotal = () => {
    if (hooks.activeTab === 0) return hooks.poData.total;
    if (hooks.activeTab === 1) return hooks.quarData.total;
    if (hooks.activeTab === 2) return hooks.relData.total;
    return hooks.errData.total;
  };

  const currentPage = () => {
    if (hooks.activeTab === 0) return hooks.poPage;
    if (hooks.activeTab === 1) return hooks.quarPage;
    if (hooks.activeTab === 2) return hooks.relPage;
    return hooks.errPage;
  };

  const currentPageChange = (model: { page: number; pageSize: number }) => {
    if (hooks.activeTab === 0) return hooks.onPoPageChange(model);
    if (hooks.activeTab === 1) return hooks.onQuarPageChange(model);
    if (hooks.activeTab === 2) return hooks.onRelPageChange(model);
    return hooks.onErrPageChange(model);
  };

  const currentColumns = () => {
    if (hooks.activeTab === 0) return poColumns;
    if (hooks.activeTab === 1) return quarantinedColumns;
    if (hooks.activeTab === 2) return releasedColumns;
    return errorColumns;
  };

  const emptyLabel = () => {
    if (hooks.activeTab === 0) return 'Ingresa una PO para buscar sus series';
    if (hooks.activeTab === 1) return 'No hay series en cuarentena';
    if (hooks.activeTab === 2) return 'No hay series liberadas en los últimos 7 días';
    return 'No hay errores de validación registrados';
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="p-6 flex flex-col gap-4">
      <Breadcrumb
        sections={[{ label: 'Izzi' }, { label: 'Consulta de series' }]}
        description="Busca, audita y gestiona las series registradas en el sistema"
        actions={
          <Tooltip title="Actualizar datos">
            <span>
              <IconButton onClick={hooks.onRefresh} disabled={hooks.isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Card variant="outlined">
        <CardContent>
          <Tabs
            value={hooks.activeTab}
            onChange={hooks.onTabChange}
            sx={{ mb: 3, borderBottom: '1px solid #f3f4f6' }}
          >
            <Tab
              label="Por PO"
              icon={<ListAltIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="En cuarentena"
              icon={<WarningAmberIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Liberadas (7 días)"
              icon={<CheckCircleIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Errores"
              icon={<ErrorOutlineIcon fontSize="small" />}
              iconPosition="start"
            />
          </Tabs>

          {showFilter && (
            <div className="flex gap-3 mb-4 items-center flex-wrap">
              <TextField
                label={filterLabel}
                variant="filled"
                size="small"
                value={hooks.filterInput}
                onChange={(e) => hooks.onFilterChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') hooks.onSearch();
                }}
                sx={{ minWidth: 280 }}
              />
              <Button
                variant="contained"
                onClick={hooks.onSearch}
                startIcon={<SearchIcon />}
                size="medium"
                disabled={hooks.isLoading}
              >
                Buscar
              </Button>
              {hooks.appliedFilter && (
                <>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="medium"
                    onClick={hooks.onClearFilter}
                    disabled={hooks.isLoading}
                  >
                    Limpiar filtro
                  </Button>
                  <Chip
                    label={`PO: ${hooks.appliedFilter}`}
                    size="small"
                    onDelete={hooks.onClearFilter}
                  />
                </>
              )}
            </div>
          )}

          <AgaveDataGrid
            rows={currentRows()}
            columns={currentColumns() as GridColDef[]}
            rowHeight={52}
            paginationMode="server"
            rowCount={currentTotal()}
            paginationModel={currentPage()}
            onPaginationModelChange={currentPageChange}
            pageSizeOptions={[10, 20, 50]}
            loading={hooks.isLoading}
            sx={{ height: 520, ...GRID_SX }}
            localeText={{ noRowsLabel: emptyLabel() }}
            onRowClick={
              hooks.activeTab === 3
                ? (params) => hooks.onOpenModal(params.row.izziMessage as string)
                : undefined
            }
          />
        </CardContent>
      </Card>

      {/* Modal de detalle de error (tab 3) */}
      <Dialog open={hooks.isModalOpen} onClose={hooks.onCloseModal} maxWidth="md" fullWidth>
        <DialogTitle className="font-bold text-[#12344A]">Detalle del error</DialogTitle>
        <DialogContent dividers className="bg-white">
          <pre className="whitespace-pre-wrap text-sm text-red-600 bg-red-50 p-4 rounded-md font-mono border border-red-200 leading-relaxed">
            {hooks.selectedError ?? ''}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={hooks.onCloseModal} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
